import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Pagination,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import TimelineIcon from '@mui/icons-material/Timeline';
import { Link as RouterLink } from 'react-router-dom';
import { ListItemSkeleton, StatCardsSkeleton } from '../components/UI/PageSkeleton';
import { DriverActivityResponse } from '../types';
import { fetchDriverActivity } from '../services/driverWorkspaceService';
import { confirmRideTaken } from '../services/leadAccessService';
import { reportLead } from '../services/driverLeadService';
import api from '../services/api';

const actionLabels: Record<string, string> = {
  unlocked: 'Unlocked',
  saved: 'Saved',
  hidden: 'Hidden',
  won: 'Won',
  lost: 'Lost',
  reported_invalid: 'Invalid contact report',
  reported_not_relevant: 'Not relevant',
  called: 'Called',
  whatsapp_clicked: 'WhatsApp opened',
};

const Activity: React.FC = () => {
  const [data, setData] = useState<DriverActivityResponse | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = async (nextPage = page) => {
    setLoading(true);
    try {
      const response = await fetchDriverActivity(nextPage, 25);
      setData(response);
    } catch {
      setMessage('Unable to load activity.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const downloadCsv = async () => {
    try {
      const response = await api.get('/api/lead-access/export-csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'my-rides.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setMessage('Unable to export CSV.');
    }
  };

  const markTaken = async (rideId: string) => {
    setWorkingId(rideId);
    try {
      await confirmRideTaken(rideId);
      setMessage('Ride marked as taken.');
      await load();
    } catch (error: any) {
      setMessage(error?.response?.data?.message || 'Unable to mark ride taken.');
    } finally {
      setWorkingId(null);
    }
  };

  const markInvalid = async (rideId: string) => {
    setWorkingId(rideId);
    try {
      await reportLead(rideId, 'invalid_contact', 'Driver reported invalid contact after unlock');
      setMessage('Invalid ride report submitted.');
      await load();
    } catch {
      setMessage('Unable to report ride.');
    } finally {
      setWorkingId(null);
    }
  };

  return (
    <Stack spacing={3} sx={{ maxWidth: 1120, mx: 'auto' }}>
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
          <Box>
            <Typography variant="h5" fontWeight={900}>Activity and unlocked rides</Typography>
            <Typography variant="body2" color="text.secondary">Track unlocked rides, saved rides, reports, and booking outcomes.</Typography>
          </Box>
          <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={downloadCsv}>Export CSV</Button>
        </Box>
      </Paper>

      {data && (
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}><Paper variant="outlined" sx={{ p: 2 }}><Typography variant="caption" color="text.secondary">Unlocked</Typography><Typography variant="h4" fontWeight={900}>{data.stats.unlocked}</Typography></Paper></Grid>
          <Grid item xs={6} md={3}><Paper variant="outlined" sx={{ p: 2 }}><Typography variant="caption" color="text.secondary">Saved</Typography><Typography variant="h4" fontWeight={900}>{data.stats.saved}</Typography></Paper></Grid>
          <Grid item xs={6} md={3}><Paper variant="outlined" sx={{ p: 2 }}><Typography variant="caption" color="text.secondary">Won</Typography><Typography variant="h4" fontWeight={900}>{data.stats.won}</Typography></Paper></Grid>
          <Grid item xs={6} md={3}><Paper variant="outlined" sx={{ p: 2 }}><Typography variant="caption" color="text.secondary">Reports</Typography><Typography variant="h4" fontWeight={900}>{data.stats.reports}</Typography></Paper></Grid>
        </Grid>
      )}

      {loading ? (
        <Stack spacing={3}>
          <StatCardsSkeleton count={4} />
          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <ListItemSkeleton rows={5} />
          </Paper>
        </Stack>
      ) : !data?.items.length ? (
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center' }}>
          <TimelineIcon sx={{ fontSize: 52, color: 'rgba(7,24,45,0.18)', mb: 1.5 }} />
          <Typography variant="h6" fontWeight={700} mb={0.75}>No activity yet</Typography>
          <Typography color="text.secondary" mb={2.5}>
            Start by browsing live rides. Once you unlock, save, or report a ride it will appear here.
          </Typography>
          <Button component={RouterLink} to="/app/rides" variant="contained" color="secondary">Browse live rides</Button>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Stack divider={<Divider />}>
            {data.items.map((item) => {
              const ride = item.ride;
              const isUnlocked = item.type === 'unlocked';
              return (
                <Box key={item.id} p={2.25}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <Chip label={actionLabels[item.type] || item.type.replace(/_/g, ' ')} size="small" color={isUnlocked ? 'success' : item.type.includes('reported') ? 'warning' : 'default'} sx={{ mb: 1 }} />
                      <Typography fontWeight={900}>{ride?.from || 'Pickup'} to {ride?.to || 'Drop'}</Typography>
                      <Typography variant="caption" color="text.secondary">{ride?.time || 'Flexible'} · {new Date(item.createdAt).toLocaleString()}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={5} md={4}>
                      {isUnlocked ? (
                        <>
                          <Typography variant="caption" color="text.secondary" display="block">Unlocked ride</Typography>
                          <Typography variant="body2">{ride?.contactName || 'Rider'} · {ride?.contactNumber || 'Not available'}</Typography>
                          <Typography variant="caption" color="text.secondary">{ride?.price || 'Price not shared'}</Typography>
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">{item.description || item.status || 'Recorded'}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={7} md={4}>
                      {isUnlocked && ride?.id && (
                        <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent={{ md: 'flex-end' }}>
                          {!['accepted', 'booked', 'completed', 'rejected'].includes(ride.status) && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              startIcon={<CheckCircleIcon />}
                              disabled={workingId === ride.id}
                              onClick={() => markTaken(ride.id)}
                            >
                              Took ride
                            </Button>
                          )}
                          <Button
                            size="small"
                            color="warning"
                            variant="text"
                            startIcon={<ReportProblemIcon />}
                            disabled={workingId === ride.id}
                            onClick={() => markInvalid(ride.id)}
                          >
                            Invalid
                          </Button>
                        </Stack>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              );
            })}
          </Stack>
        </Paper>
      )}

      {(data?.pagination.pages ?? 0) > 1 && (
        <Box display="flex" justifyContent="center">
          <Pagination count={data!.pagination.pages} page={page} onChange={(_, next) => setPage(next)} />
        </Box>
      )}

      <Snackbar open={Boolean(message)} autoHideDuration={3500} onClose={() => setMessage(null)} message={message} />
    </Stack>
  );
};

export default Activity;
